
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Divider,
  Chip,
  LinearProgress,
  Stack,
  Alert,
} from "@mui/material";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import HeaderMenu from "../Header/HeaderMenu";
import axiosApi from "../Utils/axiosApi";

const COLORS = {
  primary: "#2563eb",
  secondary: "#64748b",
  success: "#16a34a",
  warning: "#d97706",
  error: "#dc2626",
  purple: "#7c3aed",
  background: "#f5f7fb",
  border: "#e5eaf2",
  text: "#172033",
  muted: "#64748b",
};

const SalesDashboard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSalesDashboard = async () => {
    try {
      setError(null);

      const res = await axiosApi.post(
        "/dashBoard/getSalesSummaryDashboard",
        {}
      );

      const dto = res.data ?? {};

      const monthlyLeadTarget = dto.monthlyLeadTarget ?? 50;
      const monthlyHostelTarget = dto.monthlyHostelTarget ?? 15;
      const monthlyContactedTarget = dto.monthlyContactedTarget ?? 50;
      const monthlyDemoCompletedTarget =
        dto.monthlyDemoCompletedTarget ?? 20;

      const monthlyLeads = dto.monthlyLeads ?? 0;
      const monthlyContactedCount = dto.monthlyContactedCount ?? 0;
      const monthlyDemoCompletedCount =
        dto.monthlyDemoCompletedCount ?? 0;
      const monthlyRegisteredCount = dto.monthlyRegisteredCount ?? 0;
      const monthlyHostelsAdded = dto.monthlyHostelsAdded ?? 0;

      const contactedAchievement =
        monthlyContactedTarget > 0
          ? Math.round(
              (monthlyContactedCount * 100) /
                monthlyContactedTarget
            )
          : 0;

      const demoAchievement =
        monthlyDemoCompletedTarget > 0
          ? Math.round(
              (monthlyDemoCompletedCount * 100) /
                monthlyDemoCompletedTarget
            )
          : 0;

      const leadAchievementPercent = Math.round(
        (contactedAchievement + demoAchievement) / 2
      );

      const hostelAchievementPercent =
        monthlyHostelTarget > 0
          ? Math.min(
              Math.round(
                (monthlyHostelsAdded * 100) /
                  monthlyHostelTarget
              ),
              100
            )
          : 0;

      const registrationConversion =
        monthlyContactedCount > 0
          ? Math.round(
              (monthlyRegisteredCount * 100) /
                monthlyContactedCount
            )
          : 0;

      setData([
        {
          companyName: dto.companyName ?? "Sales Overview",

          monthlyContactedTarget,
          monthlyDemoCompletedTarget,

          totalProperties: dto.totalProperties ?? 0,
          totalLeads: dto.totalLeads ?? 0,
          weeklyLeads: dto.weeklyLeads ?? 0,

          monthlyLeads,
          monthlyContactedCount,
          monthlyDemoCompletedCount,
          monthlyRegisteredCount,
          monthlyHostelsAdded,

          monthlyLeadTarget,
          monthlyHostelTarget,

          contactedAchievement,
          demoAchievement,
          leadAchievementPercent,
          hostelAchievementPercent,
          registrationConversion,

          performanceScore: dto.performanceScore ?? 0,
          performanceStatus: dto.performanceStatus ?? "Critical",

          myContactedCount: dto.myContactedCount ?? 0,
          myDemoCompletedCount: dto.myDemoCompletedCount ?? 0,
          myRegisteredCount: dto.myRegisteredCount ?? 0,
          myConversionRate: dto.myConversionRate ?? 0,

          leadStatusSummary: {
            NEW_LEAD: dto.leadStatusSummary?.NEW_LEAD ?? 0,
            CONTACTED: dto.leadStatusSummary?.CONTACTED ?? 0,
            DEMO_COMPLETED:
              dto.leadStatusSummary?.DEMO_COMPLETED ?? 0,
            REGISTERED: dto.leadStatusSummary?.REGISTERED ?? 0,
            REJECTED: dto.leadStatusSummary?.REJECTED ?? 0,
            LOST: dto.leadStatusSummary?.LOST ?? 0,
          },

          prioritySummary: {
            HIGH: dto.prioritySummary?.HIGH ?? 0,
            MEDIUM: dto.prioritySummary?.MEDIUM ?? 0,
            LOW: dto.prioritySummary?.LOW ?? 0,
          },

          lastXDaysEnquiries: dto.lastXDaysEnquiries ?? 0,
        },
      ]);
    } catch (e) {
      console.error("Sales dashboard error:", e);
      setError("Unable to load sales dashboard. Please try again.");
    }
  };

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      await fetchSalesDashboard();
      setLoading(false);
    };

    loadDashboard();
  }, []);

  if (loading) {
    return (
      <>
        <HeaderMenu />
        <Box
          minHeight="70vh"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <CircularProgress size={42} />
          <Typography color="text.secondary">
            Loading sales dashboard...
          </Typography>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <HeaderMenu />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Alert
            severity="error"
            action={
              <Chip
                label="Retry"
                clickable
                color="error"
                variant="outlined"
                onClick={async () => {
                  setLoading(true);
                  await fetchSalesDashboard();
                  setLoading(false);
                }}
              />
            }
          >
            {error}
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <HeaderMenu />

      <Box
        sx={{
          minHeight: "100vh",
          background: COLORS.background,
          px: { xs: 1.5, sm: 2.5, md: 3.5 },
          py: { xs: 2, md: 3 },
        }}
      >
        {data.map((d, index) => (
          <Box key={index} sx={{ maxWidth: 1600, mx: "auto" }}>
            {/* PAGE HEADER */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: 3,
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: COLORS.text,
                    fontSize: { xs: 22, sm: 27, md: 30 },
                    letterSpacing: "-0.7px",
                  }}
                >
                  Sales & CRM Dashboard
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: COLORS.muted, mt: 0.6 }}
                >
                  {d.companyName} · Monitor sales activities,
                  leads and performance
                </Typography>
              </Box>

              <Chip
                label="Sales Overview"
                color="primary"
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  borderRadius: 2,
                  background: "#eff6ff",
                }}
              />
            </Box>

            {/* KPI SECTION */}
            <SectionHeading
              title="Business Overview"
              subtitle="Your overall sales and registration metrics"
            />

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Properties"
                  value={d.totalProperties}
                  subtitle="Properties in the system"
                  accent="#7c3aed"
                  icon="🏢"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Leads"
                  value={d.totalLeads}
                  subtitle="All CRM leads"
                  accent="#2563eb"
                  icon="👥"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Weekly Leads"
                  value={d.weeklyLeads}
                  subtitle="Leads received this week"
                  accent="#0891b2"
                  icon="📅"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Monthly Leads"
                  value={d.monthlyLeads}
                  subtitle={`Monthly target: ${d.monthlyLeadTarget}`}
                  accent="#9333ea"
                  icon="📈"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Monthly Contacted"
                  value={d.monthlyContactedCount}
                  subtitle={`Target: ${d.monthlyContactedTarget}`}
                  accent="#0284c7"
                  icon="📞"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Demo Completed"
                  value={d.monthlyDemoCompletedCount}
                  subtitle={`Target: ${d.monthlyDemoCompletedTarget}`}
                  accent="#d97706"
                  icon="🖥️"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Monthly Registered"
                  value={d.monthlyRegisteredCount}
                  subtitle="Successful registrations"
                  accent="#16a34a"
                  icon="✅"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Registration Conversion"
                  value={`${d.registrationConversion}%`}
                  subtitle="Registered / Contacted"
                  accent="#059669"
                  icon="🎯"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Hostels Added"
                  value={d.monthlyHostelsAdded}
                  subtitle={`Monthly target: ${d.monthlyHostelTarget}`}
                  accent="#ea580c"
                  icon="🏨"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Sales Performance"
                  value={`${d.performanceScore}%`}
                  subtitle="Overall performance score"
                  accent="#16a34a"
                  icon="🏆"
                  footer={
                    <StatusChip status={d.performanceStatus} />
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Lead Achievement"
                  value={`${d.leadAchievementPercent}%`}
                  subtitle="Contacted and demo achievement"
                  accent="#2563eb"
                  progress={d.leadAchievementPercent}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Hostel Target Completion"
                  value={`${d.hostelAchievementPercent}%`}
                  subtitle="Hostels added against target"
                  accent="#7c3aed"
                  progress={d.hostelAchievementPercent}
                />
              </Grid>
            </Grid>

            {/* MY PERFORMANCE */}
            <SectionHeading
              title="My Performance"
              subtitle="Individual sales activities and conversion"
            />

            <Card sx={sectionCardSx}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                      title="My Contacted"
                      value={d.myContactedCount}
                      accent="#2563eb"
                      icon="📞"
                      compact
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                      title="My Demo Completed"
                      value={d.myDemoCompletedCount}
                      accent="#d97706"
                      icon="🖥️"
                      compact
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                      title="My Registrations"
                      value={d.myRegisteredCount}
                      accent="#16a34a"
                      icon="✅"
                      compact
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                      title="My Conversion Rate"
                      value={`${d.myConversionRate}%`}
                      accent="#7c3aed"
                      icon="🎯"
                      compact
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* MY CRM ACTIVITIES CHART */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <SectionCard
                  title="My CRM Activities"
                  subtitle="Individual activity breakdown"
                >
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={[
                        {
                          name: "Contacted",
                          count: d.myContactedCount,
                        },
                        {
                          name: "Demo",
                          count: d.myDemoCompletedCount,
                        },
                        {
                          name: "Registered",
                          count: d.myRegisteredCount,
                        },
                      ]}
                      margin={{
                        top: 10,
                        right: 10,
                        left: -15,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#e8edf5"
                      />
                      <XAxis
                        dataKey="name"
                        tick={{ fill: COLORS.muted, fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: COLORS.muted, fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="count"
                        fill={COLORS.primary}
                        name="Count"
                        radius={[6, 6, 0, 0]}
                        maxBarSize={55}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </SectionCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <SectionCard
                  title="Sales Performance"
                  subtitle="Monthly targets compared with achieved results"
                >
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={[
                        {
                          name: "Contacted",
                          target: d.monthlyContactedTarget,
                          achieved: d.monthlyContactedCount,
                        },
                        {
                          name: "Demo",
                          target: d.monthlyDemoCompletedTarget,
                          achieved: d.monthlyDemoCompletedCount,
                        },
                        {
                          name: "Hostels",
                          target: d.monthlyHostelTarget,
                          achieved: d.monthlyHostelsAdded,
                        },
                      ]}
                      margin={{
                        top: 10,
                        right: 10,
                        left: -15,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#e8edf5"
                      />
                      <XAxis
                        dataKey="name"
                        tick={{ fill: COLORS.muted, fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: COLORS.muted, fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="target"
                        fill="#cbd5e1"
                        name="Target"
                        radius={[5, 5, 0, 0]}
                      />
                      <Bar
                        dataKey="achieved"
                        fill={COLORS.success}
                        name="Achieved"
                        radius={[5, 5, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </SectionCard>
              </Grid>
            </Grid>

            {/* CRM LEADS */}
            <SectionHeading
              title="CRM Leads"
              subtitle="Lead status and priority distribution"
            />

            <Card sx={sectionCardSx}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <SummaryValue
                      label="Total Leads"
                      value={d.totalLeads}
                      color={COLORS.primary}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <SummaryValue
                      label="Weekly Leads"
                      value={d.weeklyLeads}
                      color="#0891b2"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <SummaryValue
                      label="Monthly Leads"
                      value={d.monthlyLeads}
                      color={COLORS.purple}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2.5 }} />

                <Typography
                  fontWeight={750}
                  color={COLORS.text}
                  mb={1.5}
                >
                  Lead Status Summary
                </Typography>

                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={1}
                  sx={{ mb: 2.5 }}
                >
                  <Chip
                    color="primary"
                    label={`NEW LEAD (${d.leadStatusSummary.NEW_LEAD})`}
                  />
                  <Chip
                    color="info"
                    label={`CONTACTED (${d.leadStatusSummary.CONTACTED})`}
                  />
                  <Chip
                    color="warning"
                    label={`DEMO COMPLETED (${d.leadStatusSummary.DEMO_COMPLETED})`}
                  />
                  <Chip
                    color="success"
                    label={`REGISTERED (${d.leadStatusSummary.REGISTERED})`}
                  />
                  <Chip
                    color="error"
                    label={`REJECTED (${d.leadStatusSummary.REJECTED})`}
                  />
                  <Chip
                    variant="outlined"
                    label={`LOST (${d.leadStatusSummary.LOST})`}
                  />
                </Stack>

                <Divider sx={{ my: 2.5 }} />

                <Typography
                  fontWeight={750}
                  color={COLORS.text}
                  mb={1.5}
                >
                  Lead Priority Summary
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <PriorityCard
                      label="High Priority"
                      value={d.prioritySummary.HIGH}
                      color="#dc2626"
                      background="#fef2f2"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <PriorityCard
                      label="Medium Priority"
                      value={d.prioritySummary.MEDIUM}
                      color="#d97706"
                      background="#fffbeb"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <PriorityCard
                      label="Low Priority"
                      value={d.prioritySummary.LOW}
                      color="#16a34a"
                      background="#f0fdf4"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* PERFORMANCE SUMMARY */}
            <SectionHeading
              title="Performance Summary"
              subtitle="Review target achievement and overall progress"
            />

            <Card sx={sectionCardSx}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <PerformanceBlock
                      title="Lead Performance"
                      color={COLORS.primary}
                    >
                      <TargetRow
                        label="Contacted"
                        achieved={d.monthlyContactedCount}
                        target={d.monthlyContactedTarget}
                        percentage={d.contactedAchievement}
                      />

                      <TargetRow
                        label="Demo Completed"
                        achieved={d.monthlyDemoCompletedCount}
                        target={d.monthlyDemoCompletedTarget}
                        percentage={d.demoAchievement}
                      />

                      <TargetRow
                        label="Registration Conversion"
                        achieved={`${d.registrationConversion}%`}
                      />

                      <Box sx={{ mt: 2 }}>
                        <Typography
                          fontWeight={750}
                          color={COLORS.text}
                          mb={1}
                        >
                          Overall Lead Achievement
                        </Typography>
                        <ProgressBar
                          value={d.leadAchievementPercent}
                          color={COLORS.primary}
                        />
                        <StatusChip
                          status={
                            d.leadAchievementPercent >= 100
                              ? "Target Met"
                              : "Target Not Met"
                          }
                        />
                      </Box>
                    </PerformanceBlock>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <PerformanceBlock
                      title="Hostel Performance"
                      color={COLORS.success}
                    >
                      <TargetRow
                        label="Hostel Target"
                        target={d.monthlyHostelTarget}
                      />

                      <TargetRow
                        label="Hostels Added"
                        achieved={d.monthlyHostelsAdded}
                        target={d.monthlyHostelTarget}
                        percentage={d.hostelAchievementPercent}
                      />

                      <Box sx={{ mt: 2 }}>
                        <Typography
                          fontWeight={750}
                          color={COLORS.text}
                          mb={1}
                        >
                          Hostel Target Achievement
                        </Typography>
                        <ProgressBar
                          value={d.hostelAchievementPercent}
                          color={COLORS.success}
                        />
                        <StatusChip
                          status={
                            d.hostelAchievementPercent >= 100
                              ? "Target Met"
                              : "Target Not Met"
                          }
                        />
                      </Box>
                    </PerformanceBlock>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <PerformanceBlock
                      title="Overall Performance"
                      color={COLORS.purple}
                    >
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Overall Score
                        </Typography>
                        <Typography
                          variant="h3"
                          fontWeight={800}
                          color={COLORS.text}
                        >
                          {d.performanceScore}%
                        </Typography>
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={1}
                      >
                        Target Completion
                      </Typography>

                      <Typography
                        variant="h5"
                        fontWeight={800}
                        color={COLORS.text}
                      >
                        {Math.round(
                          (d.leadAchievementPercent +
                            d.hostelAchievementPercent) /
                            2
                        )}
                        %
                      </Typography>

                      <Box sx={{ mt: 2 }}>
                        <ProgressBar
                          value={Math.round(
                            (d.leadAchievementPercent +
                              d.hostelAchievementPercent) /
                              2
                          )}
                          color={COLORS.purple}
                        />
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <StatusChip status={d.performanceStatus} />
                      </Box>
                    </PerformanceBlock>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ENQUIRIES */}
            <SectionHeading
              title="Enquiries & Registrations"
              subtitle="Recent enquiry activity and hostel additions"
            />

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <MetricCard
                  title="Enquiries (Last 45 Days)"
                  value={d.lastXDaysEnquiries}
                  subtitle="Enquiry activity over the last 45 days"
                  accent="#0284c7"
                  icon="📩"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MetricCard
                  title="Hostels Added This Month"
                  value={d.monthlyHostelsAdded}
                  subtitle={`Monthly target: ${d.monthlyHostelTarget}`}
                  accent="#16a34a"
                  icon="🏨"
                  progress={d.hostelAchievementPercent}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                textAlign: "center",
                py: 1,
                color: COLORS.muted,
              }}
            >
              <Typography variant="caption">
                Sales & CRM Dashboard
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};

/* ================= REUSABLE COMPONENTS ================= */

const sectionCardSx = {
  borderRadius: 3,
  border: `1px solid ${COLORS.border}`,
  boxShadow: "0 3px 14px rgba(15, 23, 42, 0.045)",
  mb: 3,
  overflow: "hidden",
};

const SectionHeading = ({ title, subtitle }) => (
  <Box sx={{ mb: 1.8 }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 800,
        color: COLORS.text,
        fontSize: { xs: 17, md: 19 },
      }}
    >
      {title}
    </Typography>

    {subtitle && (
      <Typography
        variant="body2"
        sx={{ color: COLORS.muted, mt: 0.4 }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const MetricCard = ({
  title,
  value,
  subtitle,
  accent = COLORS.primary,
  icon,
  progress,
  footer,
  compact = false,
}) => (
  <Card
    sx={{
      height: "100%",
      minHeight: compact ? 120 : 145,
      borderRadius: 3,
      border: `1px solid ${COLORS.border}`,
      boxShadow: "0 3px 12px rgba(15, 23, 42, 0.045)",
      transition: "all .2s ease",
      position: "relative",
      overflow: "hidden",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: "0 8px 22px rgba(15, 23, 42, 0.09)",
      },
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: 4,
        height: "100%",
        background: accent,
      }}
    />

    <CardContent sx={{ p: compact ? 2 : 2.2 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.muted,
              fontWeight: 650,
              fontSize: 12.5,
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              color: COLORS.text,
              fontWeight: 800,
              fontSize: compact ? 27 : 31,
              lineHeight: 1.3,
              mt: 1,
              wordBreak: "break-word",
            }}
          >
            {value}
          </Typography>
        </Box>

        {icon && (
          <Box
            sx={{
              width: 39,
              height: 39,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `${accent}14`,
              fontSize: 19,
            }}
          >
            {icon}
          </Box>
        )}
      </Stack>

      {subtitle && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: COLORS.muted,
            mt: 0.7,
          }}
        >
          {subtitle}
        </Typography>
      )}

      {progress !== undefined && (
        <Box sx={{ mt: 1.5 }}>
          <ProgressBar value={progress} color={accent} />
        </Box>
      )}

      {footer && <Box sx={{ mt: 1 }}>{footer}</Box>}
    </CardContent>
  </Card>
);

const SectionCard = ({ title, subtitle, children }) => (
  <Card sx={sectionCardSx}>
    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
      <Typography
        variant="h6"
        fontWeight={800}
        color={COLORS.text}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.4, mb: 2 }}
        >
          {subtitle}
        </Typography>
      )}

      {children}
    </CardContent>
  </Card>
);

const SummaryValue = ({ label, value, color }) => (
  <Box
    sx={{
      p: 2,
      height: "100%",
      borderRadius: 2,
      background: "#f8fafc",
      border: `1px solid ${COLORS.border}`,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>

    <Typography
      variant="h5"
      fontWeight={800}
      sx={{ color, mt: 0.5 }}
    >
      {value}
    </Typography>
  </Box>
);

const PriorityCard = ({ label, value, color, background }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      background,
      border: `1px solid ${color}25`,
    }}
  >
    <Typography
      variant="body2"
      sx={{ color, fontWeight: 700 }}
    >
      {label}
    </Typography>

    <Typography
      variant="h4"
      sx={{ color, fontWeight: 800, mt: 0.5 }}
    >
      {value}
    </Typography>
  </Box>
);

const PerformanceBlock = ({ title, color, children }) => (
  <Box
    sx={{
      height: "100%",
      p: 2,
      borderRadius: 2.5,
      border: `1px solid ${COLORS.border}`,
      background: "#fff",
    }}
  >
    <Box
      sx={{
        borderLeft: `4px solid ${color}`,
        pl: 1.2,
        mb: 2,
      }}
    >
      <Typography fontWeight={800} color={COLORS.text}>
        {title}
      </Typography>
    </Box>

    {children}
  </Box>
);

const TargetRow = ({
  label,
  achieved,
  target,
  percentage,
}) => (
  <Box sx={{ mb: 1.8 }}>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={750}
        color={COLORS.text}
      >
        {achieved !== undefined && target !== undefined
          ? `${achieved} / ${target}`
          : achieved ?? "-"}
      </Typography>
    </Stack>

    {percentage !== undefined && (
      <Box sx={{ mt: 0.8 }}>
        <ProgressBar value={percentage} color={COLORS.primary} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.4 }}
        >
          {percentage}% achieved
        </Typography>
      </Box>
    )}
  </Box>
);

const ProgressBar = ({ value, color }) => {
  const safeValue = Math.max(0, Math.min(Number(value) || 0, 100));

  return (
    <LinearProgress
      variant="determinate"
      value={safeValue}
      sx={{
        height: 8,
        borderRadius: 5,
        backgroundColor: "#e9eef5",
        "& .MuiLinearProgress-bar": {
          borderRadius: 5,
          backgroundColor: color,
        },
      }}
    />
  );
};

const StatusChip = ({ status }) => {
  const color =
    status === "Target Met"
      ? "success"
      : status === "On Track"
      ? "primary"
      : status === "Needs Improvement"
      ? "warning"
      : status === "Target Not Met"
      ? "warning"
      : "error";

  return (
    <Chip
      label={status}
      color={color}
      size="small"
      sx={{
        mt: 1,
        fontWeight: 700,
        borderRadius: 1.5,
      }}
    />
  );
};

export default SalesDashboard;
